import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Close, CalendarToday, DateRange, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Transaction } from '../services/plaidService';
import { cachedSpendingByCategory } from '../services/prefetch';

interface SpendingData {
  category: string;
  amount: number;
  color: string;
  percentage: number;
}

interface SpendingBreakdownProps {
  embedded?: boolean;
}

const SpendingBreakdown: React.FC<SpendingBreakdownProps> = ({ embedded = false }) => {
  const [spendingData, setSpendingData] = useState<SpendingData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalSpending, setTotalSpending] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categoryTransactions, setCategoryTransactions] = useState<Transaction[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [viewMode, setViewMode] = useState<'month' | 'year'>('month');

  // Mercury muted earth-tone palette
  const colors = [
    '#0f7a5a', // forest green
    '#5b4d3a', // warm taupe
    '#4a5b8a', // dusty indigo
    '#b8842a', // amber
    '#c43e3e', // muted brick
    '#7a6a8a', // mauve
    '#0a6478', // deep teal
    '#8a6a3a', // bronze
    '#6c7a89', // steel
    '#9a7a5a', // sand
  ];

  // Enhanced category mapping for transactions
  const categoryMap: { [key: string]: string } = {
    // Plaid format categories (new)
    'FOOD_AND_DRINK': 'Food & Drink',
    'FOOD_AND_DRINK_GROCERIES': 'Food & Drink',
    'FOOD_AND_DRINK_RESTAURANTS': 'Food & Drink',
    'FOOD_AND_DRINK_FAST_FOOD': 'Food & Drink',
    'FOOD_AND_DRINK_COFFEE_SHOPS': 'Food & Drink',
    'FOOD_AND_DRINK_BARS': 'Food & Drink',
    
    'TRANSPORTATION': 'Transportation',
    'TRANSPORTATION_GAS_STATIONS': 'Transportation',
    'TRANSPORTATION_RIDESHARE': 'Transportation',
    'TRANSPORTATION_PUBLIC_TRANSIT': 'Transportation',
    'TRANSPORTATION_TAXIS': 'Transportation',
    'TRANSPORTATION_AUTOMOTIVE': 'Transportation',
    
    'SHOPS': 'Shopping',
    'SHOPS_GENERAL_MERCHANDISE': 'Shopping',
    'SHOPS_ELECTRONICS': 'Shopping',
    'SHOPS_HOME_IMPROVEMENT': 'Shopping',
    'SHOPS_CLOTHING': 'Shopping',
    'SHOPS_BOOKS': 'Shopping',
    
    'GENERAL_MERCHANDISE': 'Shopping',
    'GENERAL_MERCHANDISE_OTHER_GENERAL_MERCHANDISE': 'Shopping',
    
    'ENTERTAINMENT': 'Entertainment',
    'ENTERTAINMENT_STREAMING_SERVICES': 'Entertainment',
    'ENTERTAINMENT_MOVIES': 'Entertainment',
    'ENTERTAINMENT_GAMING': 'Entertainment',
    'ENTERTAINMENT_MUSIC': 'Entertainment',
    
    'HEALTHCARE': 'Healthcare',
    'HEALTHCARE_PHARMACY': 'Healthcare',
    'HEALTHCARE_MEDICAL': 'Healthcare',
    'HEALTHCARE_DENTAL': 'Healthcare',
    'HEALTHCARE_VISION': 'Healthcare',
    
    'UTILITIES': 'Utilities',
    'UTILITIES_ELECTRIC': 'Utilities',
    'UTILITIES_WATER': 'Utilities',
    'UTILITIES_INTERNET': 'Utilities',
    'UTILITIES_PHONE': 'Utilities',
    'UTILITIES_GAS': 'Utilities',
    
    'INSURANCE': 'Insurance',
    'INSURANCE_AUTO': 'Insurance',
    'INSURANCE_HEALTH': 'Insurance',
    'INSURANCE_HOME': 'Insurance',
    'INSURANCE_LIFE': 'Insurance',
    
    'EDUCATION': 'Education',
    'EDUCATION_ONLINE_LEARNING': 'Education',
    'EDUCATION_BOOKS': 'Education',
    'EDUCATION_TUITION': 'Education',
    
    'TRAVEL': 'Travel',
    'TRAVEL_FLIGHTS': 'Travel',
    'TRAVEL_HOTELS': 'Travel',
    'TRAVEL_RENTAL_CARS': 'Travel',
    'TRAVEL_CRUISES': 'Travel',
    'TRAVEL_RAILWAYS': 'Travel',
    'TRAVEL_TAXIS': 'Travel',
    'TRAVEL_RIDESHARE': 'Travel',
    
    'RENT_AND_UTILITIES': 'Rent & Utilities',
    'RENT_AND_UTILITIES_GAS_AND_ELECTRICITY': 'Rent & Utilities',
    'RENT_AND_UTILITIES_ELECTRIC': 'Rent & Utilities',
    'RENT_AND_UTILITIES_WATER': 'Rent & Utilities',
    'RENT_AND_UTILITIES_INTERNET_AND_CABLE': 'Rent & Utilities',
    'RENT_AND_UTILITIES_TELEPHONE': 'Rent & Utilities',
    'RENT_AND_UTILITIES_RENT': 'Rent & Utilities',
    'RENT_AND_UTILITIES_OTHER_UTILITIES': 'Rent & Utilities',
    'RENT_AND_UTILITIES_SEWAGE_AND_WASTE_MANAGEMENT': 'Rent & Utilities',

    'GENERAL_SERVICES': 'Services',
    'GENERAL_SERVICES_OTHER_GENERAL_SERVICES': 'Services',
    'GENERAL_SERVICES_ACCOUNTING_AND_FINANCIAL_PLANNING': 'Services',
    'GENERAL_SERVICES_AUTOMOTIVE': 'Services',
    'GENERAL_SERVICES_CHILDCARE': 'Services',
    'GENERAL_SERVICES_CONSULTING_AND_LEGAL': 'Services',
    'GENERAL_SERVICES_EDUCATION': 'Services',
    'GENERAL_SERVICES_INSURANCE': 'Services',
    'GENERAL_SERVICES_POSTAGE_AND_SHIPPING': 'Services',
    'GENERAL_SERVICES_STORAGE': 'Services',

    'PERSONAL_CARE': 'Personal Care',
    'PERSONAL_CARE_GYMS_AND_FITNESS_CENTERS': 'Personal Care',
    'PERSONAL_CARE_HAIR_AND_BEAUTY': 'Personal Care',
    'PERSONAL_CARE_LAUNDRY_AND_DRY_CLEANING': 'Personal Care',

    'GENERAL_MERCHANDISE_TOBACCO_AND_VAPE': 'Shopping',
    'GENERAL_MERCHANDISE_SUPERSTORES': 'Shopping',
    'GENERAL_MERCHANDISE_DISCOUNT_STORES': 'Shopping',
    'GENERAL_MERCHANDISE_ONLINE_MARKETPLACES': 'Shopping',
    'GENERAL_MERCHANDISE_PET_SUPPLIES': 'Shopping',
    'GENERAL_MERCHANDISE_SPORTING_GOODS': 'Shopping',

    'LOAN_PAYMENTS': 'Loan Payments',

    'OTHER': 'Other',
    'OTHER_OTHER': 'Other',
    'BANKING': 'Other',
    'CHARITY': 'Other',
    'GOVERNMENT': 'Other',
    'GOVERNMENT_AND_NON_PROFIT': 'Other',
    
    // Legacy format categories (fallback)
    'Food and Drink': 'Food & Drink',
    'Groceries': 'Food & Drink',
    'Restaurants': 'Food & Drink',
    'Fast Food': 'Food & Drink',
    'Transportation': 'Transportation',
    'Gas Stations': 'Transportation',
    'Rideshare': 'Transportation',
    'Public Transit': 'Transportation',
    'Shops': 'Shopping',
    'General Merchandise': 'Shopping',
    'Electronics': 'Shopping',
    'Home Improvement': 'Shopping',
    'Entertainment': 'Entertainment',
    'Streaming Services': 'Entertainment',
    'Movies': 'Entertainment',
    'Gaming': 'Entertainment',
    'Healthcare': 'Healthcare',
    'Pharmacy': 'Healthcare',
    'Medical': 'Healthcare',
    'Utilities': 'Utilities',
    'Electric': 'Utilities',
    'Water': 'Utilities',
    'Internet': 'Utilities',
    'Phone': 'Utilities',
    'Gas': 'Utilities',
    'Insurance': 'Insurance',
    'Auto': 'Insurance',
    'Health': 'Insurance',
    'Home': 'Insurance',
    'Life': 'Insurance',
    'Education': 'Education',
    'Online Learning': 'Education',
    'Books': 'Education',
    'Other': 'Other',
    'Banking': 'Other',
    'Charity': 'Other',
    'Government': 'Other',
  };

  useEffect(() => {
    fetchSpendingData();
  }, [selectedMonth, selectedYear, viewMode]);

  const fetchSpendingData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let startDate: Date;
      let endDate: Date;

      if (viewMode === 'month') {
        startDate = new Date(selectedYear, selectedMonth, 1);
        endDate = new Date(selectedYear, selectedMonth + 1, 0); // Last day of the month
      } else {
        // Year view: full calendar year
        startDate = new Date(selectedYear, 0, 1);
        endDate = new Date(selectedYear, 11, 31);
      }
      
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      // Use /spending/by-category — server-side grouped, includes every spending
      const response = await cachedSpendingByCategory(startDateStr, endDateStr);
      const categories = response.categories || [];

      // Flatten per-category transactions for the drill-down dialog
      const transactions = categories.flatMap((c) => c.transactions || []);
      setAllTransactions(transactions);

      // Collapse primary categories into display buckets via categoryMap
      const categoryTotals: { [key: string]: number } = {};
      categories.forEach((c) => {
        const mappedCategory = categoryMap[c.category] || 'Other';
        categoryTotals[mappedCategory] = (categoryTotals[mappedCategory] || 0) + c.total;
      });

      const total = response.total ?? Object.values(categoryTotals).reduce((sum, a) => sum + a, 0);
      const data = Object.entries(categoryTotals)
        .map(([category, amount], index) => ({
          category,
          amount: Math.round(amount * 100) / 100,
          color: colors[index % colors.length],
          percentage: total > 0 ? Math.round((amount / total) * 100 * 100) / 100 : 0,
        }))
        .sort((a, b) => b.amount - a.amount);

      setSpendingData(data);
      setTotalSpending(total);
    } catch (err) {
      console.error('Error fetching spending data:', err);
      setError('Failed to load spending data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getAccountType = (accountId: string) => {
    // This would need to be passed from parent or fetched separately
    // For now, we'll use a simple approach
    return 'unknown';
  };

  const getDisplayAmount = (transaction: Transaction) => {
    // Use the same amount logic as other components
    return -transaction.amount; // Invert to get user-friendly view
  };

  const getCategoryForTransaction = (transaction: Transaction) => {
    let mappedCategory = 'Other';
    
    if (transaction.personal_finance_category) {
      const primary = transaction.personal_finance_category.primary;
      const detailed = transaction.personal_finance_category.detailed;
      
      mappedCategory = categoryMap[primary] || 'Other';
      
      if (mappedCategory === 'Other' && detailed) {
        mappedCategory = categoryMap[detailed] || 'Other';
      }
    } else if (transaction.category && transaction.category.length > 0) {
      const firstCategory = transaction.category[0];
      mappedCategory = categoryMap[firstCategory] || 'Other';
      
      if (mappedCategory === 'Other' && transaction.category.length > 1) {
        const secondCategory = transaction.category[1];
        mappedCategory = categoryMap[secondCategory] || 'Other';
      }
    }
    
    return mappedCategory;
  };

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    
    // Filter transactions for this category
    const filteredTransactions = allTransactions.filter(transaction => {
      const transactionCategory = getCategoryForTransaction(transaction);
      return transactionCategory === category;
    });
    
    setCategoryTransactions(filteredTransactions);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCategory(null);
    setCategoryTransactions([]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getMonthName = (monthIndex: number) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  };

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'month' | 'year' | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const stepPeriod = (delta: number) => {
    if (viewMode === 'month') {
      const next = new Date(selectedYear, selectedMonth + delta, 1);
      setSelectedMonth(next.getMonth());
      setSelectedYear(next.getFullYear());
    } else {
      setSelectedYear(selectedYear + delta);
    }
  };

  const isAtCurrent = () => {
    const now = new Date();
    if (viewMode === 'month') {
      return selectedYear === now.getFullYear() && selectedMonth === now.getMonth();
    }
    return selectedYear === now.getFullYear();
  };

  const getDateRangeText = () => {
    if (viewMode === 'month') {
      return `${getMonthName(selectedMonth)} ${selectedYear}`;
    }
    return `${selectedYear}`;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #eaeaea',
            borderRadius: 2,
            px: 1.5,
            py: 1,
            boxShadow: '0 4px 12px rgba(15,15,15,0.06)',
            minWidth: 140,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: data.color }} />
            <Typography variant="caption" sx={{ color: '#8a8a8a', fontWeight: 500 }}>
              {data.category}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#0a0a0a', fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(data.amount)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#8a8a8a', fontVariantNumeric: 'tabular-nums' }}>
            {data.percentage}% of total
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    embedded ? (
      <Box sx={{ mb: 4 }} className="animate-fade-in">{children}</Box>
    ) : (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }} className="animate-fade-in">{children}</Container>
    );

  return (
    <Wrapper>
      {!embedded && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 600,
              letterSpacing: '-0.025em',
              color: '#0a0a0a',
              mb: 0.5,
            }}
          >
            Spending Breakdown
          </Typography>
          <Typography variant="body2" sx={{ color: '#5c5c5c' }}>
            Analyze your spending patterns by category
          </Typography>
        </Box>
      )}

      {/* View Mode Toggle and Date Selectors */}
      <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {embedded && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#0a0a0a' }}>
                Spending Breakdown
              </Typography>
              <Typography variant="caption" sx={{ color: '#8a8a8a' }}>
                Analyze your spending patterns by category
              </Typography>
            </Box>
          )}
          {/* View Mode Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#5c5c5c' }}>
              View Mode
            </Typography>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{
                background: '#f5f5f5',
                borderRadius: 1.5,
                p: 0.375,
                border: '1px solid #eaeaea',
                gap: 0,
                '& .MuiToggleButton-root': {
                  px: 1.5,
                  py: 0.5,
                  fontSize: 12,
                  fontWeight: 500,
                  border: 'none',
                  borderRadius: 1,
                  color: '#5c5c5c',
                  textTransform: 'none',
                  transition: 'background-color 0.15s ease, color 0.15s ease',
                  '&:hover': { background: 'rgba(10,10,10,0.04)' },
                  '&.Mui-selected': {
                    color: '#0a0a0a',
                    fontWeight: 600,
                    background: '#ffffff',
                    boxShadow: '0 1px 2px rgba(15,15,15,0.06)',
                    '&:hover': { background: '#ffffff' },
                  },
                },
              }}
            >
              <ToggleButton value="month">
                <CalendarToday sx={{ mr: 0.75, fontSize: 14 }} />
                Month
              </ToggleButton>
              <ToggleButton value="year">
                <DateRange sx={{ mr: 0.75, fontSize: 14 }} />
                Year
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Period Stepper */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => stepPeriod(-1)}
              sx={{
                border: '1px solid #eaeaea',
                borderRadius: 1.5,
                color: '#5c5c5c',
                '&:hover': { background: 'rgba(10,10,10,0.04)' },
              }}
              aria-label="Previous period"
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Box
              sx={{
                minWidth: 180,
                textAlign: 'center',
                px: 2,
                py: 0.75,
                border: '1px solid #eaeaea',
                borderRadius: 1.5,
                bgcolor: '#ffffff',
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#0a0a0a', fontVariantNumeric: 'tabular-nums' }}>
                {getDateRangeText()}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => stepPeriod(1)}
              disabled={isAtCurrent()}
              sx={{
                border: '1px solid #eaeaea',
                borderRadius: 1.5,
                color: '#5c5c5c',
                '&:hover': { background: 'rgba(10,10,10,0.04)' },
                '&.Mui-disabled': { color: '#cfcfcf', borderColor: '#f0f0f0' },
              }}
              aria-label="Next period"
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>

          {/* Date Range Display */}
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              bgcolor: '#fafaf6',
              border: '1px solid #eaeaea',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'flex-end' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ color: '#8a8a8a', fontWeight: 500, display: 'block', mb: 0.25 }}>
                Viewing period
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '0.95rem', color: '#0a0a0a' }}>
                {getDateRangeText()}
              </Typography>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography variant="caption" sx={{ color: '#8a8a8a', fontWeight: 500, display: 'block', mb: 0.25 }}>
                Total spending
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '1.375rem', letterSpacing: '-0.015em', color: '#0a0a0a', fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(totalSpending)}
              </Typography>
            </Box>
          </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : spendingData.length === 0 ? (
        /* No Data State */
        <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <CardContent sx={{ p: 4 }}>
            <Box textAlign="center">
              <Typography variant="h6" gutterBottom sx={{ color: '#5c5c5c', fontWeight: 600 }}>
                No spending data found
              </Typography>
              <Typography variant="body2" sx={{ color: '#8a8a8a', mb: 2 }}>
                No transactions found for the selected period. Try selecting a different date range or link your account to see spending data.
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Tip: Use the date controls above to view spending from different months
              </Alert>
            </Box>
          </CardContent>
        </Card>
      ) : (
        /* Data Display */
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Donut Chart */}
          <Box sx={{ flex: { xs: 1, md: 5 } }}>
            <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', height: '100%' }}>
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#5c5c5c', mb: 0.5 }}>
                    Spending by Category
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#8a8a8a' }}>
                    {getDateRangeText()}
                  </Typography>
                </Box>
                <Box sx={{ position: 'relative', height: 360, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={spendingData}
                        cx="50%"
                        cy="50%"
                        innerRadius={88}
                        outerRadius={134}
                        paddingAngle={1.5}
                        dataKey="amount"
                        nameKey="category"
                        stroke="#ffffff"
                        strokeWidth={2}
                        onClick={(data: any) => handleCategoryClick(data.category)}
                      >
                        {spendingData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            style={{ cursor: 'pointer', outline: 'none' }}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} cursor={false} />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Center label */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#8a8a8a', fontWeight: 500, display: 'block' }}>
                      Total
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '1.5rem', letterSpacing: '-0.02em', color: '#0a0a0a', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                      {formatCurrency(totalSpending)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#8a8a8a', fontVariantNumeric: 'tabular-nums' }}>
                      {spendingData.length} categor{spendingData.length === 1 ? 'y' : 'ies'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Category List */}
          <Box sx={{ flex: { xs: 1, md: 4 } }}>
            <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', height: '100%' }}>
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#5c5c5c', mb: 0.5 }}>
                    Category Details
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#8a8a8a' }}>
                    Click a category to see transactions
                  </Typography>
                </Box>
                <Box sx={{ maxHeight: 400, overflowY: 'auto', mx: -1 }}>
                  {spendingData.map((item, idx) => {
                    const isLast = idx === spendingData.length - 1;
                    return (
                      <Box
                        key={item.category}
                        onClick={() => handleCategoryClick(item.category)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 1,
                          py: 1.25,
                          borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
                          cursor: 'pointer',
                          transition: 'background-color 0.12s ease',
                          '&:hover': { bgcolor: '#fafafa' },
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            backgroundColor: item.color,
                            borderRadius: '50%',
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#0a0a0a' }} noWrap>
                            {item.category}
                          </Typography>
                          {/* Inline progress bar */}
                          <Box sx={{ mt: 0.5, height: 2, bgcolor: '#f0f0f0', borderRadius: 1, overflow: 'hidden' }}>
                            <Box
                              sx={{
                                width: `${item.percentage}%`,
                                height: '100%',
                                bgcolor: item.color,
                                borderRadius: 1,
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 92 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0a0a0a', fontVariantNumeric: 'tabular-nums' }}>
                            {formatCurrency(item.amount)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#8a8a8a', fontVariantNumeric: 'tabular-nums' }}>
                            {item.percentage}%
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Category Transactions Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {selectedCategory} Transactions ({categoryTransactions.length})
            </Typography>
            <IconButton onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {categoryTransactions.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Merchant</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categoryTransactions.map((transaction) => (
                    <TableRow key={transaction.transaction_id}>
                      <TableCell>
                        {formatDate(transaction.date)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {transaction.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {transaction.merchant_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={selectedCategory} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          color="error.main"
                        >
                          {formatCurrency(Math.abs(getDisplayAmount(transaction)))}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No transactions found for this category
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
};

export default SpendingBreakdown;
